import React, { useState } from 'react';
import { Link, navigate } from '@reach/router';
import styled from 'styled-components';
import Text from 'components/Text';
import Logo from 'components/Logo';
import { BlueButton } from 'components/Button';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { sendResetPasswordEmail } from 'api/auth';

const ForgotPasswordPage = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  background: #e8eaeb;
`;

const ForgotPasswordPanel = styled.form`
  min-height: 450px;
  min-width: 400px;
  background-color: #ffffff;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-top: 80px;
  padding: 35px 45px 25px;
  display: flex;
  flex-direction: column;
  align-content: center;
  text-align: center;
`;

const ForgotPasswordHeader = styled.div`
  display: inline-block;
  margin: 20px;
`;

const InputContainer = styled.div`
  margin: 0.5em auto;
  max-width: 300px;
  text-align: left;
  width: 100%;
`;

const TextInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 32px;
  padding: 2px 8px;
  font-size: 16px;
  border-radius: 2px;
  border: ${({ border }) => border || '1px solid #E5E7EB'};
  outline: none;

  ::placeholder {
    color: #4a4a4a;
    opacity: 0.5;
  }
`;

const ButtonContainer = styled.div`
  margin: 1.5em 0;
  text-align: center;
`;

const LoginLink = styled(Link)`
  text-decoration: none;
  color: #0779af;
  font-size: 14px;
  font-style: italic;
`;

const ErrorMessage = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: #ea6e6d;
  padding-top: 1em;
  white-space: pre-wrap;
`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value;

    setEmail(value);
  };

  const handleSubmit = async event => {
    event.preventDefault();

    try {
      await sendResetPasswordEmail(email);
      setShowSuccessModal(true);
    } catch (err) {
      console.log('err :', err);
      setFormError(
        'An unexpected error has occured.\nPlease try again or contact a system administrator if the problem persists.'
      );
    }
  };

  return (
    <ForgotPasswordPage>
      <ForgotPasswordPanel onSubmit={handleSubmit}>
        <Logo
          width="110px"
          height="50px"
          display="inline-block"
          margin="0.5em auto"
        />
        <ForgotPasswordHeader>
          <Text fontSize="22px">Reset Your Password</Text>
        </ForgotPasswordHeader>

        <InputContainer>
          <Text fontSize="12px" color="#4A4A4A" margin="0.3em 0">
            Email Address
          </Text>
          <TextInput
            name="email"
            type="email"
            required
            onChange={handleInputChange}
          />

          {formError ? (
            <React.Fragment>
              <ErrorMessage>{formError}</ErrorMessage>
            </React.Fragment>
          ) : null}
        </InputContainer>

        <ButtonContainer>
          <BlueButton
            type="submit"
            padding="9px 24px"
            buttonHeight="30px"
            fontSize="14px"
          >
            Send Password Reset Link
          </BlueButton>
        </ButtonContainer>

        <LoginLink to="/login">Back to Log In</LoginLink>
      </ForgotPasswordPanel>

      {showSuccessModal ? (
        <ConfirmationModal
          updateModalState={() => navigate('/login')}
          header="Password Reset"
          message="A link to reset your password has been sent to your account email address"
        />
      ) : null}
    </ForgotPasswordPage>
  );
};

export default ForgotPassword;
